import React from 'react';

// The privacy policy the App Store listing points at. Written to match
// what the product actually does — update it when data practices change.
const PrivacyPolicy = () => (
  <div className="min-h-screen bg-gray-900 text-gray-200">
    <div className="max-w-3xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold text-white mb-2">JukeHive Privacy Policy</h1>
      <p className="text-sm text-gray-400 mb-8">Last updated: August 9, 2026</p>

      <div className="space-y-8 text-sm leading-relaxed">
        <section>
          <h2 className="text-xl font-semibold text-white mb-2">The short version</h2>
          <p>
            JukeHive is a party. We collect the minimum needed to run one: who you are at
            the party, where the party is, and what music the room likes. Parties are
            temporary — sessions and everything inside them (chat, direct messages,
            photos, the queue) are deleted within 24 hours of ending. Direct messages
            vanish even faster: about an hour after they're read. We don't sell data,
            show ads, or share your information with data brokers.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-2">What we collect</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-white">Account information.</strong> Guests are anonymous —
              just a display name you choose. Hosts sign in with Apple or email, which gives
              us your email address (or Apple's private relay address) and the name you provide.
            </li>
            <li>
              <strong className="text-white">Location.</strong> Used at the moment you create or
              join a party, to anchor the party to a place and check you're in range. We store
              the party's location while the party exists; we do not track your movements or
              keep a location history.
            </li>
            <li>
              <strong className="text-white">Music taste.</strong> Songs you suggest and upvote
              build a private taste profile (capped and recency-weighted) so the app can offer
              you your go-to songs. It's visible only to you and deleted with your account.
            </li>
            <li>
              <strong className="text-white">Party content.</strong> Chat messages, photos,
              votes, reactions, and song suggestions live inside the party session and are
              deleted with it (within 24 hours of the party ending). Direct messages are
              private to the two participants — not visible to hosts or anyone else — and are
              deleted roughly one hour after being read.
            </li>
            <li>
              <strong className="text-white">Party history.</strong> If you host with an
              account, a summary of each party (name, dates, counts, top songs) is kept on
              your profile so the app can show your hosting record. Deleted with your account.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-2">What we don't do</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>No selling or renting of personal data. No advertising or ad tracking.</li>
            <li>No reading of your Apple Music or Spotify library — playback for hosts uses
              your own subscription on your own device.</li>
            <li>No background location tracking.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-2">Service providers</h2>
          <p>
            JukeHive runs on Google Firebase (authentication and database), Railway
            (application hosting), and Vercel (website hosting). Song search uses the Apple
            Music and Spotify catalog APIs — searches are proxied by our server and are not
            tied to advertising profiles.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-2">Deleting your data</h2>
          <p>
            Delete your account any time in the app: <em>Profile → Delete Account</em>. This
            permanently erases your account, party history, taste profile, anthems, and
            preferences, and ends any party you're hosting. Guests' anonymous data dissolves
            with the parties they attended. You can also email us (below) and we'll delete
            your data within 30 days.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-2">Reporting content</h2>
          <p>
            Every message and conversation can be reported in the app (long-press a message
            → Report). Reports come straight to the team and are reviewed promptly. You can
            also mute anyone so their messages no longer appear on your device, and hosts
            can block disruptive guests.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-2">Children</h2>
          <p>
            JukeHive is not directed at children under 13, and we don't knowingly collect
            their data. Hosts control an explicit-content filter for their parties.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-2">Contact</h2>
          <p>
            Questions or data requests: <a className="text-purple-400" href="mailto:eugenew.evans@gmail.com">eugenew.evans@gmail.com</a>.
            Changes to this policy will be posted on this page with an updated date.
          </p>
        </section>
      </div>
    </div>
  </div>
);

export default PrivacyPolicy;
